/**
 * Page 1: Your Body Right Now
 *
 * Personalized body state summary based on life_stage and struggles.
 * Feature: 017-body-insight-summary
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { BodyInsightProgress } from '../../components/body-insight/BodyInsightProgress';
import { getPageContent } from '../../services/bodyInsightTemplates';

export const Page1BodyRightNow: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Get personalized content for this page
  const content = user ? getPageContent(1, user.profile as any) : null;

  if (!content) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#FFF5E9] to-[#F8F4EF]">
        <p className="text-gray-600">Unable to load personalized content.</p>
      </div>
    );
  }

  const handleNext = () => {
    navigate('/body-insight/2');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFF5E9] to-[#F8F4EF] py-8 px-4">
      <div className="max-w-[800px] mx-auto">
        {/* Progress indicator */}
        <BodyInsightProgress currentPage={1} totalPages={5} />

        {/* Main content card */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
          {/* Headline */}
          <p className="text-sm uppercase tracking-wide text-[#B7A76D] mb-2 font-semibold">
            Here's what we understand about you
          </p>

          {/* Page title */}
          <h1 className="font-['Nunito'] text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            YOUR BODY RIGHT NOW
          </h1>

          {/* Body content */}
          <div className="space-y-6">
            {/* Main headline */}
            {content.headline && (
              <h2 className="font-['Nunito'] text-2xl font-semibold text-[#C98CCC] mb-4">
                {content.headline}
              </h2>
            )}

            {/* Body text */}
            {content.body && (
              <p className="font-['Inter'] text-lg leading-relaxed text-gray-700">
                {content.body}
              </p>
            )}

            {/* List items */}
            {content.listItems && content.listItems.length > 0 && (
              <ul className="space-y-3 mt-6">
                {content.listItems.map((item, index) => (
                  <li
                    key={index}
                    className="flex items-start font-['Inter'] text-base text-gray-700"
                  >
                    <span className="text-[#B7A76D] mr-3 text-xl flex-shrink-0">•</span>
                    <span className="leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Medical disclaimer */}
        <div className="bg-white/50 rounded-lg p-4 mb-6 border border-gray-200">
          <p className="text-sm text-gray-600 text-center font-['Inter']">
            💡 This guidance is educational and does not replace medical advice
          </p>
        </div>

        {/* Navigation button */}
        <div className="flex justify-end">
          <button
            onClick={handleNext}
            className="bg-gradient-to-r from-[#C98CCC] to-[#B7A76D] text-white font-semibold py-3 px-8 rounded-full hover:shadow-lg transition-all duration-200 transform hover:scale-105"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default Page1BodyRightNow;
